import { Stack } from '@mui/material';
import { endOfMonth, startOfMonth } from 'date-fns';
import { useState } from 'react';

import {
  calendarResourceTypes,
  createCalendarColors,
  createCalendarResources,
  initFilterParams,
  ResourceCalendar,
  ResourceCalendarFilters,
} from '../../components/general';
import { CalendarEvent } from '../../components/general/ResourceCalendar';
import {
  CalendarGruppenraumResource,
  CalendarSeminarraumResource,
  CalendarStandortResource,
} from '../../components/general/ResourceCalendar/helperClasses/CalendarResource';
import { type Zeitraum } from '../../dtos';
import { trpc } from '../../trpc';
import { sessionStorageApi, sessionStorageKeys } from '../../utils';
import { groupRaumResources, type RaumCollapseInfo } from './RaumRessourcenOverviewCalendar.utils';

export const RaumRessourcenOverviewCalendar = () => {
  const initDate = new Date();

  const [timeRange, setTimeRange] = useState(() => ({
    start: startOfMonth(initDate),
    end: endOfMonth(initDate),
  }));
  const [filterParams, setFilterParams] = useState(() =>
    initFilterParams(sessionStorageApi.get(sessionStorageKeys.RAUMRESOURCEOVERVIEW_FILTER)),
  );
  const [collapseInfos, setCollapseInfos] = useState<RaumCollapseInfo>({
    seminarraum: false,
    gruppenraum: false,
    standort: false,
  });

  const locationsWithRaeume = trpc.planung.resources.getLocationsWithRaeume.useQuery(
    {
      ...filterParams,
      ...timeRange,
    },
    { retry: false, refetchInterval: 60_000, refetchIntervalInBackground: true },
  );

  const standorte = trpc.planung.resources.getStandorte.useQuery(
    {
      standortSapIds: filterParams.standortSapIds,
      ...timeRange,
    },
    { retry: false, refetchInterval: 60_000, refetchIntervalInBackground: true },
  );

  let seminarraumResources: CalendarSeminarraumResource[] = [];
  let gruppenraumResources: CalendarGruppenraumResource[] = [];
  let standortResources: CalendarStandortResource[] = [];
  let events: CalendarEvent[] = [];

  if (locationsWithRaeume.data && standorte.data) {
    ({ seminarraumResources, gruppenraumResources, standortResources } = createCalendarResources(
      false,
      [],
      locationsWithRaeume.data,
      standorte.data,
      timeRange.start,
    ));
    const resources = [...seminarraumResources, ...gruppenraumResources, ...standortResources];
    events = resources.flatMap((resource) => resource.createFixedEventsFromBlockungen(null));
  }

  const handleExpansionChange = (resourceId: string, collapsed: boolean): void => {
    setCollapseInfos((prev): RaumCollapseInfo => {
      switch (resourceId) {
        case calendarResourceTypes.SEMINARRAUM:
          return { ...prev, seminarraum: collapsed };
        case calendarResourceTypes.GRUPPENRAUM:
          return { ...prev, gruppenraum: collapsed };
        case calendarResourceTypes.STANDORT:
          return { ...prev, standort: collapsed };
        default:
          return prev;
      }
    });
  };

  const onCalendarTimeRangeChanged = (newTimeRange: Zeitraum): void => {
    setTimeRange(newTimeRange);
  };

  return (
    <Stack p={2} spacing={2}>
      <ResourceCalendarFilters
        filterParameters={filterParams}
        startYear={timeRange.start.getFullYear()}
        onFilterChange={(filter) => {
          sessionStorageApi.set(sessionStorageKeys.RAUMRESOURCEOVERVIEW_FILTER, filter);
          setFilterParams(filter);
        }}
      />
      {/*
        This height is important, the calendar needs to be contained in a container with a height, see
        https://mobiscroll.com/docs/react/eventcalendar/api#opt-view.

        Without this height, https://jira.haufe.io/browse/AKAVM-2370 will occur again.
      */}
      <div className="h-[calc(100vh-100px)]">
        <ResourceCalendar
          resources={groupRaumResources(seminarraumResources, gruppenraumResources, standortResources, collapseInfos)}
          events={events}
          colors={createCalendarColors({
            seminarraumResources,
            gruppenraumResources,
            standortResources,
            timeRange,
          })}
          timeRange={timeRange}
          toggleExpansion={handleExpansionChange}
          initialDate={initDate}
          timeRangeChangeHandler={onCalendarTimeRangeChanged}
        />
      </div>
    </Stack>
  );
};
